import { Routes } from '@angular/router';
import { P500ExtComponent } from './pages/error/p500.ext.component';
import {
  AdminJobComponent,
  AdminJobDetailComponent,
  JobComponent,
  JobDetailComponent,
} from '@nimmsta/ng-workflow-lib/job';
import { WorkflowDataStoreComponent } from '@nimmsta/ng-workflow-lib/workflow-data-store';
import {
  DataConnectionComponent,
  DataConnectionTemplatesComponent,
} from '@nimmsta/ng-workflow-lib/dataconnection';
import {
  DeviceComponent,
  LightTagDetailComponent,
  MdmDetailComponent,
  ScannerDetailComponent,
} from '@nimmsta/ng-workflow-lib/device';
import { EmailComponent } from '@nimmsta/ng-workflow-lib/workflow-email';
import {
  SoftwareArtifactReleaseTypeComponent,
  SoftwareArtifactSingleComponent,
  SoftwareDetailComponent,
  SoftwareListComponent,
  SoftwareOverviewComponent,
  SoftwarePlatformComponent,
  SoftwareTypeComponent,
} from '@nimmsta/ng-workflow-lib/software';
import {
  WorkflowComponent,
  WorkflowDefinitionComponent,
  WorkflowDetailComponent,
  WorkflowExecutionComponent,
  WorkflowExecutionDetailComponent,
  WorkflowTemplateComponent,
} from '@nimmsta/ng-workflow-lib/workflow';
import {
  DisableBackGuard,
  hasAuthorizationGuard,
  isAdminGuard,
  isAuthenticatedGuard,
} from '@nimmsta/ng-commons-lib/auth';
import { LicensesComponent } from '@nimmsta/ng-commons-lib/licenses';
import {
  ProfileComponent,
  ProfileSessionComponent,
} from '@nimmsta/ng-commons-lib/profile';
import { UserRole } from '@nimmsta/ng-commons-lib/models';
import { WaitingListComponent } from '@nimmsta/ng-commons-lib/waiting-list';
import {
  AllUserComponent,
  UserOverviewComponent,
} from '@nimmsta/ng-b2b-lib/user';
import {
  OrganizationsComponent,
  OrganizationSettingsComponent,
  OrganizationDetailsComponent,
} from '@nimmsta/ng-b2b-lib/organizations';
import { ConfigurationCustomerWrapperComponent } from '@nimmsta/ng-b2b-lib/configurations';

import { LayoutComponent } from './layout/layout.component';
import { RequestAccessComponent } from './pages/request-access/request-access.component';
import { DashboardComponent } from './pages/dashboard/dashboard.component';
import { EditDashboardComponent } from './pages/dashboard/edit/edit-dashboard.component';
import { SupportComponent } from './pages/support/support.component';
import { InformationComponent } from './pages/information/information.component';
import { InformationItemComponent } from './pages/information/information-item.component';
import { P404ExtComponent } from './pages/error/p404.ext.component';
import { PartnerOrganizationsComponent } from './pages/partner-organization/partner-organizations.component';
import { PartnerUserOverviewComponent } from './pages/partner-organization/partner-user-overview.component';

export const routes: Routes = [
  {
    path: '',
    redirectTo: 'dashboard',
    pathMatch: 'full',
  },
  {
    path: '500',
    component: P500ExtComponent,
  },
  {
    path: 'register',
    redirectTo: 'request-access',
  },
  {
    path: 'request-access',
    component: RequestAccessComponent,
  },
  {
    path: 'waiting-list',
    component: WaitingListComponent,
  },
  {
    path: '',
    component: LayoutComponent,
    canActivate: [isAuthenticatedGuard],
    children: [
      {
        path: 'dashboard',
        component: DashboardComponent,
        canActivate: [hasAuthorizationGuard],
        data: {
          auth: { anyRoles: [UserRole.ADMIN, UserRole.MANAGER] },
          redirectToFirstVisibleNav: true,
        },
      },
      {
        path: 'dashboard-edit',
        component: EditDashboardComponent,
        canActivate: [hasAuthorizationGuard],
        data: {
          auth: { anyRoles: [UserRole.ADMIN, UserRole.MANAGER] },
          redirectToFirstVisibleNav: true,
        },
      },
      {
        path: 'workflows',
        component: WorkflowComponent,
        canActivate: [hasAuthorizationGuard],
        data: { auth: { anyPermissions: ['workflow.read'] } },
      },
      {
        path: 'workflows/:id/definition',
        canDeactivate: [DisableBackGuard],
        component: WorkflowDefinitionComponent,
        canActivate: [hasAuthorizationGuard],
        data: { auth: { anyPermissions: ['workflow.write'] } },
      },
      {
        path: 'workflow-templates',
        component: WorkflowTemplateComponent,
        canActivate: [isAdminGuard],
      },
      {
        path: 'workflow-templates/:id/dashboard-edit',
        component: EditDashboardComponent,
        canActivate: [isAdminGuard],
      },
      {
        path: 'workflows/:id',
        component: WorkflowDetailComponent,
        canActivate: [hasAuthorizationGuard],
        data: { auth: { anyPermissions: ['workflow.read'] } },
      },
      {
        path: 'workflows/:workflow_id/executions',
        component: WorkflowExecutionComponent,
        canActivate: [hasAuthorizationGuard],
        data: { auth: { anyPermissions: ['job.read'] } },
      },
      {
        path: 'workflows/:workflow_id/executions/:id',
        component: WorkflowExecutionDetailComponent,
        canActivate: [hasAuthorizationGuard],
        data: { auth: { anyPermissions: ['job.read'] } },
      },
      {
        path: 'workflow-emails',
        component: EmailComponent,
        canActivate: [hasAuthorizationGuard],
        data: {
          auth: { anyPermissions: ['workflow.read', 'workflow.write'] },
          redirectToFirstVisibleNav: true,
        },
      },
      {
        path: 'users',
        component: UserOverviewComponent,
        canActivate: [hasAuthorizationGuard],
        data: {
          auth: {
            anyRoles: [UserRole.ADMIN, UserRole.MANAGER],
            anyPermissions: ['worker.read', 'worker.write'],
          },
        },
      },
      {
        path: 'users-all',
        component: AllUserComponent,
        canActivate: [isAdminGuard],
      },
      {
        path: 'dataconnection',
        component: DataConnectionComponent,
        canActivate: [hasAuthorizationGuard],
        data: { auth: { anyPermissions: ['data_connection.read'] } },
      },
      {
        path: 'workflow-data-stores',
        component: WorkflowDataStoreComponent,
        canActivate: [hasAuthorizationGuard],
        data: { auth: { anyPermissions: ['data_connection.read'] } },
      },
      {
        path: 'dataconnection-templates',
        component: DataConnectionTemplatesComponent,
        canActivate: [isAdminGuard],
      },
      {
        path: 'jobs',
        component: JobComponent,
        canActivate: [hasAuthorizationGuard],
        data: { auth: { anyPermissions: ['job.read'] } },
      },
      {
        path: 'jobs/:id',
        component: JobDetailComponent,
        canActivate: [hasAuthorizationGuard],
        data: { auth: { anyPermissions: ['job.read'] } },
      },
      {
        path: 'organizations',
        component: OrganizationsComponent,
        canActivate: [isAdminGuard],
      },
      {
        path: 'organizations/:id',
        component: OrganizationDetailsComponent,
        canActivate: [isAdminGuard],
      },
      {
        path: 'organizations/:id/settings',
        component: OrganizationSettingsComponent,
        canActivate: [hasAuthorizationGuard],
        data: { auth: { anyRoles: [UserRole.ADMIN, UserRole.MANAGER] } },
      },
      {
        path: 'partner-organizations',
        component: PartnerOrganizationsComponent,
        canActivate: [hasAuthorizationGuard],
        data: { auth: { anyRoles: [UserRole.ADMIN, UserRole.MANAGER] } },
      },
      {
        path: 'partner-organizations/:id',
        component: PartnerUserOverviewComponent,
        canActivate: [hasAuthorizationGuard],
        data: { auth: { anyRoles: [UserRole.ADMIN, UserRole.MANAGER] } },
      },
      {
        path: 'admin-jobs',
        component: AdminJobComponent,
        canActivate: [isAdminGuard],
      },
      {
        path: 'admin-jobs/:id',
        component: AdminJobDetailComponent,
        canActivate: [isAdminGuard],
      },
      {
        path: 'software',
        component: SoftwareOverviewComponent,
        canActivate: [hasAuthorizationGuard],
        data: {
          auth: { anyRoles: [UserRole.ADMIN, UserRole.MANAGER] },
          redirectToFirstVisibleNav: true,
        },
      },
      {
        path: 'software/types',
        component: SoftwareTypeComponent,
        canActivate: [hasAuthorizationGuard],
        data: {
          auth: { anyRoles: [UserRole.ADMIN, UserRole.MANAGER] },
          redirectToFirstVisibleNav: true,
        },
      },
      {
        path: 'software/platforms',
        component: SoftwarePlatformComponent,
        canActivate: [hasAuthorizationGuard],
        data: {
          auth: { anyRoles: [UserRole.ADMIN, UserRole.MANAGER] },
          redirectToFirstVisibleNav: true,
        },
      },
      {
        path: 'software/artifactReleaseTypes',
        component: SoftwareArtifactReleaseTypeComponent,
        canActivate: [hasAuthorizationGuard],
        data: {
          auth: { anyRoles: [UserRole.ADMIN, UserRole.MANAGER] },
          redirectToFirstVisibleNav: true,
        },
      },
      {
        path: 'software/configurations',
        component: ConfigurationCustomerWrapperComponent,
        canActivate: [hasAuthorizationGuard],
        data: {
          auth: { anyRoles: [UserRole.ADMIN, UserRole.MANAGER] },
          redirectToFirstVisibleNav: true,
        },
      },
      {
        path: 'software/:softwareId',
        component: SoftwareDetailComponent,
        canActivate: [hasAuthorizationGuard],
        data: {
          auth: { anyRoles: [UserRole.ADMIN, UserRole.MANAGER] },
          redirectToFirstVisibleNav: true,
        },
      },
      {
        path: 'software/type/:typeId',
        component: SoftwareListComponent,
        canActivate: [hasAuthorizationGuard],
        data: {
          auth: { anyRoles: [UserRole.ADMIN, UserRole.MANAGER] },
          redirectToFirstVisibleNav: true,
        },
      },
      {
        path: 'software/:softwareId/:artifactId',
        component: SoftwareArtifactSingleComponent,
        canActivate: [hasAuthorizationGuard],
        data: {
          auth: { anyRoles: [UserRole.ADMIN, UserRole.MANAGER] },
          redirectToFirstVisibleNav: true,
        },
      },
      {
        path: 'profile',
        component: ProfileComponent,
      },
      {
        path: 'profile/sessions',
        component: ProfileSessionComponent,
      },
      {
        path: 'support',
        component: SupportComponent,
      },
      {
        path: 'devices',
        component: DeviceComponent,
        canActivate: [hasAuthorizationGuard],
        data: {
          auth: { anyRoles: [UserRole.ADMIN, UserRole.MANAGER] },
          redirectToFirstVisibleNav: true,
        },
      },
      {
        path: 'devices/mdm/:id',
        component: MdmDetailComponent,
        canActivate: [hasAuthorizationGuard],
        data: {
          auth: { anyRoles: [UserRole.ADMIN, UserRole.MANAGER] },
          redirectToFirstVisibleNav: true,
        },
      },
      {
        path: 'devices/smartwatches/:id',
        component: ScannerDetailComponent,
        canActivate: [hasAuthorizationGuard],
        data: {
          auth: { anyRoles: [UserRole.ADMIN, UserRole.MANAGER] },
          redirectToFirstVisibleNav: true,
        },
      },
      {
        path: 'devices/light-tags/:id',
        component: LightTagDetailComponent,
        canActivate: [hasAuthorizationGuard],
        data: {
          auth: { anyRoles: [UserRole.ADMIN, UserRole.MANAGER] },
          redirectToFirstVisibleNav: true,
        },
      },
      {
        path: 'information',
        component: InformationComponent,
        canActivate: [hasAuthorizationGuard],
        data: {
          auth: { anyRoles: [UserRole.ADMIN, UserRole.MANAGER] },
          redirectToFirstVisibleNav: true,
        },
      },
      {
        path: 'information/:id',
        component: InformationItemComponent,
        canActivate: [hasAuthorizationGuard],
        data: {
          auth: { anyRoles: [UserRole.ADMIN, UserRole.MANAGER] },
          redirectToFirstVisibleNav: true,
        },
      },
      {
        path: 'licenses',
        component: LicensesComponent,
      },
    ],
  },
  {
    path: '**',
    component: P404ExtComponent,
  },
];
